import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminHeader } from "../components/Common/CompanyAdminHeader";
import { useAuthStore } from "../stores/auth-store";
import { companyWithdrawalApiService } from "../../infrastructure/api/company/company-withdrawal-api-service";
import { adminApiService } from "../../infrastructure/api/company/company-api-service";
import type {
  ClosingChecklist,
  WithdrawalRequest,
} from "../../domain/types/withdrawal";

/**
 * 탈퇴 화면.
 *
 * 한 라우트에서 역할에 따라 갈린다.
 *  - COMPANY_ADMIN : 사이트 운영 종료 (사전 조건 3종 → 신청 → 종료 예약 D-7 → 삭제)
 *  - 그 외         : 회원 탈퇴 (7일 유예)
 */
const Unregister: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isCompanyAdmin = user?.role === "COMPANY_ADMIN";

  const handleLogoClick = () => navigate(`/company/${user?.companyId}/admin/`);
  const handleBackClick = () => navigate("/profile");

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AdminHeader onLogoClick={handleLogoClick} showMyPageLink={true} />

      <div className="flex flex-col items-center mt-10">
        <div className="w-full max-w-[46.875rem] px-5 pb-[100px]">
          <div className="flex flex-row items-center gap-5 w-full mb-6">
            <button type="button" onClick={handleBackClick} aria-label="뒤로">
              <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
                <path
                  d="M7.12095 0.170067C7.32709 0.376209 7.34583 0.698787 7.17717 0.926099L7.12095 0.991223L2.11243 6L7.12095 11.0088C7.32709 11.2149 7.34583 11.5375 7.17717 11.7648L7.12095 11.8299C6.91481 12.0361 6.59223 12.0548 6.36492 11.8862L6.29979 11.8299L0.880438 6.41058C0.674296 6.20444 0.655556 5.88186 0.824217 5.65455L0.880438 5.58942L6.29979 0.170067C6.52655 -0.056689 6.89419 -0.056689 7.12095 0.170067Z"
                  fill="#252525"
                />
              </svg>
            </button>
            <h2 className="text-[22px] font-bold text-[#222]">
              {isCompanyAdmin ? "사이트 운영 종료" : "회원 탈퇴"}
            </h2>
          </div>
          <div className="h-[1px] bg-[#eee] mb-8" />

          {isCompanyAdmin ? <ClosingFlow /> : <MemberWithdrawal />}
        </div>
      </div>
    </div>
  );
};

/* ==================== COMPANY_ADMIN : 사이트 운영 종료 ==================== */

const ClosingFlow: React.FC = () => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  const [request, setRequest] = useState<WithdrawalRequest | null>(null);
  const [checklist, setChecklist] = useState<ClosingChecklist | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    try {
      const [myRequest, myChecklist, companyInfo] = await Promise.all([
        companyWithdrawalApiService.getMyRequest(),
        companyWithdrawalApiService.getClosingChecklist(companyId),
        adminApiService.getCompanyInfo(companyId),
      ]);

      setRequest(myRequest);
      setChecklist(myChecklist);
      setCompanyName(companyInfo.data?.companyName ?? "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <p className="text-[14px] text-[#888] py-10 text-center">불러오는 중입니다.</p>;
  }

  if (error) {
    return <Notice tone="danger">{error}</Notice>;
  }

  // 예약이 걸려 있으면 그 상태를, 없으면 신청 화면을 그린다.
  if (request?.status === "SCHEDULED") {
    return <ClosingScheduled companyId={companyId as number} request={request} />;
  }

  return (
    <ClosingRequestForm
      companyId={companyId as number}
      companyName={companyName}
      checklist={checklist}
      onChanged={load}
    />
  );
};

const ClosingRequestForm: React.FC<{
  companyId: number;
  companyName: string;
  checklist: ClosingChecklist | null;
  onChanged: () => void;
}> = ({ companyId, companyName, checklist, onChanged }) => {
  const navigate = useNavigate();
  // 준비와 신청을 한 화면에 담으면 읽을 것이 너무 많다. 준비를 마친 뒤에만 신청으로 넘긴다.
  const [step, setStep] = useState<1 | 2>(1);
  const [reason, setReason] = useState("");
  const [agreedDelete, setAgreedDelete] = useState(false);
  const [agreedBlock, setAgreedBlock] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const { downloading, download, downloadError } = useClosingExports(companyId, onChanged);

  const canRequest = checklist?.canRequest ?? false;
  const agreed = agreedDelete && agreedBlock;

  const submit = async () => {
    setError("");

    try {
      await companyWithdrawalApiService.requestClosing(reason);
      setConfirming(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "종료 신청에 실패했습니다.");
      setConfirming(false);
    }
  };

  const unmet = [
    !checklist?.noticeSatisfied && "운영 종료 공지 1개월 경과",
    !checklist?.ordersSatisfied && "사용자 주문 내역 다운로드",
    !checklist?.clientsSatisfied && "거래처 기업 정보 다운로드",
  ].filter(Boolean) as string[];

  const doneCount = [
    checklist?.noticeSatisfied,
    checklist?.ordersSatisfied,
    checklist?.clientsSatisfied,
  ].filter(Boolean).length;

  return (
    <>
      <StepHeader step={step} />

      {step === 1 ? (
        <>
          <div className="flex items-end justify-between mb-3">
            <p className="text-[13px] text-[#555]">
              아래 3가지를 마쳐야 신청할 수 있습니다.
            </p>
            <span className="text-[13px] text-[#888]">
              <b className="text-[#4285F4] font-bold">{doneCount}</b> / 3 완료
            </span>
          </div>

          <div className="flex gap-1.5 mb-6">
            {[
              checklist?.noticeSatisfied,
              checklist?.ordersSatisfied,
              checklist?.clientsSatisfied,
            ].map((done, index) => (
              <span
                key={index}
                className={`h-[4px] flex-1 rounded-full ${done ? "bg-[#4285F4]" : "bg-[#e8e8e8]"}`}
              />
            ))}
          </div>

          <ChecklistCard
            step={1}
            title="사이트 운영 종료 공지"
            satisfied={checklist?.noticeSatisfied ?? false}
            badge={noticeBadge(checklist)}
            description={noticeDescription(checklist)}
            actionLabel={checklist?.noticePublishedAt ? "공지 보기" : "공지 작성하기"}
            onAction={() =>
              navigate(
                checklist?.noticePublishedAt
                  ? `/company/${companyId}/contents/notices`
                  : // 아직 공지가 없으면 목록을 거치지 않고 종료 공지 작성 화면으로 바로 들어간다.
                    `/company/${companyId}/contents/notices?mode=closing`,
              )
            }
          />

          <ChecklistCard
            step={2}
            title="사용자 주문 내역 내려받기"
            satisfied={checklist?.ordersSatisfied ?? false}
            badge={exportBadge(checklist?.ordersExportedAt, checklist?.ordersSatisfied)}
            description="주문 상품 · 결제 · 배송 정보 전체 기간 (xlsx)"
            actionLabel={downloading === "orders" ? "내려받는 중" : "엑셀 다운로드"}
            onAction={() => download("orders")}
            disabled={downloading !== null}
          />

          <ChecklistCard
            step={3}
            title="거래처 기업 정보 내려받기"
            satisfied={checklist?.clientsSatisfied ?? false}
            badge={exportBadge(checklist?.clientsExportedAt, checklist?.clientsSatisfied)}
            description="거래 중이던 기업회원 정보 (xlsx)"
            actionLabel={downloading === "clients" ? "내려받는 중" : "엑셀 다운로드"}
            onAction={() => download("clients")}
            disabled={downloading !== null}
          />

          <p className="text-[12px] text-[#888] leading-relaxed mt-3">
            내려받은 파일은{" "}
            <b className="font-medium text-[#555]">{checklist?.exportValidDays ?? 7}일</b> 동안만
            인정됩니다. 종료 시점의 데이터와 크게 달라지지 않도록 하기 위해서입니다.
          </p>

          {(error || downloadError) && (
            <p className="text-[13px] text-[#4285F4] mt-4">{error || downloadError}</p>
          )}

          <button
            type="button"
            disabled={!canRequest}
            onClick={() => setStep(2)}
            className={`w-full py-3.5 rounded-[8px] text-[15px] font-bold mt-7 transition-colors ${
              canRequest
                ? "bg-[#222] text-white hover:bg-[#333]"
                : "bg-[#e8e8e8] text-[#bbb] cursor-not-allowed"
            }`}
          >
            다음 단계
          </button>

          {unmet.length > 0 && (
            <p className="text-[12px] text-[#888] text-center mt-3">
              준비가 남았습니다 : {unmet.join(", ")}
            </p>
          )}
        </>
      ) : (
        <>
          {/* 신청하면 벌어지는 일을 시간 순서로 한 번만 보여 준다. */}
          <div className="rounded-[12px] border border-[#e0e0e0] bg-white p-5 mb-6">
            <ul className="space-y-3">
              {[
                [
                  "신청 직후",
                  "신규 주문과 견적 문의가 차단됩니다. 진행 중인 주문은 계속 처리됩니다.",
                ],
                [
                  "7일 뒤",
                  "소속 회원 전원의 계정과 회사·쇼핑몰 데이터가 삭제됩니다. 되돌릴 수 없습니다.",
                ],
                [
                  "그 사이",
                  "언제든 종료를 취소할 수 있습니다. 취소하면 주문 차단도 함께 풀립니다.",
                ],
              ].map(([when, what]) => (
                <li key={when} className="flex gap-3 text-[13px] leading-relaxed">
                  <span className="shrink-0 w-[72px] text-[#888]">{when}</span>
                  <span className="text-[#555]">{what}</span>
                </li>
              ))}
            </ul>
            <p className="text-[12px] text-[#888] mt-4 pt-3.5 border-t border-[#f0f0f0]">
              회원 개인정보는 법정 보관을 위해 암호화 상태로 5년간 보관 후 자동 파기됩니다.
            </p>
          </div>

          <div className="mb-6">
            <span className="block text-[13px] font-medium text-[#555] mb-2">
              종료 사유 (선택)
            </span>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="관리자 검토 시 참고합니다."
              className="w-full border border-[#e0e0e0] rounded-[8px] px-3.5 py-3 text-[14px] leading-relaxed focus:border-[#4285F4] focus:outline-none"
            />
          </div>

          <AgreeCheck
            checked={agreedDelete}
            onChange={setAgreedDelete}
            label="소속 회원 전원의 계정과 회사 데이터가 삭제되는 것에 동의합니다. (필수)"
          />
          <AgreeCheck
            checked={agreedBlock}
            onChange={setAgreedBlock}
            label="신청 즉시 신규 주문이 차단되는 것에 동의합니다. (필수)"
          />

          {error && <p className="text-[13px] text-[#4285F4] mt-3">{error}</p>}

          <div className="flex gap-2 mt-7">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="px-6 py-3.5 rounded-[8px] text-[15px] font-medium bg-white text-[#222] border border-[#e0e0e0] hover:bg-[#f5f5f5]"
            >
              이전
            </button>
            <button
              type="button"
              disabled={!agreed}
              onClick={() => setConfirming(true)}
              className={`flex-1 py-3.5 rounded-[8px] text-[15px] font-bold transition-colors ${
                agreed
                  ? "bg-[#222] text-white hover:bg-[#333]"
                  : "bg-[#e8e8e8] text-[#bbb] cursor-not-allowed"
              }`}
            >
              사이트 운영 종료 신청하기
            </button>
          </div>

          <p className="text-[12px] text-[#888] text-center mt-3">
            신청하면 7일 뒤에 자동으로 종료됩니다. 그 전까지 취소할 수 있습니다.
          </p>
        </>
      )}

      {confirming && (
        <ConfirmByNameModal
          companyName={companyName}
          onClose={() => setConfirming(false)}
          onConfirm={submit}
        />
      )}
    </>
  );
};

/** 지금 어느 단계인지, 다음에 무엇이 남았는지 보여 준다. */
const StepHeader: React.FC<{ step: 1 | 2 }> = ({ step }) => (
  <div className="flex items-center gap-3 mb-7">
    {[
      { no: 1, label: "종료 준비" },
      { no: 2, label: "종료 신청" },
    ].map(({ no, label }, index) => (
      <React.Fragment key={no}>
        {index > 0 && <span className="flex-1 h-[1px] bg-[#e8e8e8]" />}
        <div className="flex items-center gap-2">
          <span
            className={`w-6 h-6 rounded-full text-[12px] font-bold flex items-center justify-center ${
              step >= no ? "bg-[#4285F4] text-white" : "bg-[#f0f0f0] text-[#bbb]"
            }`}
          >
            {no}
          </span>
          <span
            className={`text-[14px] ${
              step === no ? "text-[#222] font-bold" : "text-[#bbb]"
            }`}
          >
            {label}
          </span>
        </div>
      </React.Fragment>
    ))}
  </div>
);

const ClosingScheduled: React.FC<{
  companyId: number;
  request: WithdrawalRequest;
}> = ({ companyId, request }) => {
  const remainingDays = daysUntil(request.scheduledDeletedAt);
  const { downloading, download, downloadError } = useClosingExports(companyId);

  return (
    <div className="text-center pt-4">
      <div className="w-[52px] h-[52px] rounded-full text-[26px] flex items-center justify-center mx-auto mb-4 bg-[#f5f5f5] text-[#555]">
        !
      </div>
      <h2 className="text-[20px] font-bold text-[#222] mb-2.5">운영 종료가 예약되었습니다.</h2>
      <p className="text-[14px] text-[#555] leading-relaxed">
        예정일이 지나면 자동으로 종료 처리되며 되돌릴 수 없습니다.
      </p>

      {remainingDays !== null && (
        <div className="mt-7">
          <div className="text-[44px] font-bold text-[#222] leading-none">D-{remainingDays}</div>
          <div className="text-[13px] text-[#888] mt-2">
            {formatDateTime(request.scheduledDeletedAt)} 종료 예정
          </div>
        </div>
      )}

      <div className="h-[1px] bg-[#eee] my-8" />

      {/* 종료 전까지 자료를 다시 받을 수 있어야 한다. 안내만 하고 수단이 없으면 소용이 없다. */}
      <p className="text-[14px] font-medium text-[#222] mb-1">
        필요한 자료를 내려받아 주세요.
      </p>
      <p className="text-[13px] text-[#888] mb-4">종료 전까지 언제든 다시 받을 수 있습니다.</p>

      <div className="flex justify-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => download("orders")}
          disabled={downloading !== null}
          className="px-4 py-2.5 rounded-[8px] text-[13px] font-medium border border-[#4285F4] text-[#4285F4] hover:bg-[#E8EFFF] disabled:border-[#e0e0e0] disabled:text-[#bbb] disabled:hover:bg-white"
        >
          {downloading === "orders" ? "내려받는 중" : "사용자 주문 내역"}
        </button>
        <button
          type="button"
          onClick={() => download("clients")}
          disabled={downloading !== null}
          className="px-4 py-2.5 rounded-[8px] text-[13px] font-medium border border-[#4285F4] text-[#4285F4] hover:bg-[#E8EFFF] disabled:border-[#e0e0e0] disabled:text-[#bbb] disabled:hover:bg-white"
        >
          {downloading === "clients" ? "내려받는 중" : "거래처 기업 정보"}
        </button>
      </div>

      {downloadError && <p className="text-[13px] text-[#4285F4] mt-3">{downloadError}</p>}

      <div className="h-[1px] bg-[#eee] my-8" />

      <p className="text-[13px] text-[#555] leading-relaxed">
        진행 중인 주문은 종료 후 조회할 수 없습니다. 종료 전까지 마무리해 주세요.
        <br />
        신규 주문과 견적 문의는 계속 차단된 상태입니다.
      </p>

      <p className="text-[13px] text-[#888] leading-relaxed mt-5">
        종료 취소가 필요하시면 <b className="font-medium text-[#555]">admin@datoz.co.kr</b> 로
        문의해 주세요.
      </p>

      <p className="text-[12px] text-[#bbb] mt-8">
        {formatDateTime(request.requestedAt)} 신청
      </p>
    </div>
  );
};

/**
 * 인계 자료 내보내기.
 *
 * 신청 전에는 사전 조건을 채우려고, 예약 기간에는 종료 전에 다시 받으려고 쓴다.
 * onDownloaded 를 주면 다운로드 후 사전 조건 상태를 다시 읽는다.
 */
const useClosingExports = (companyId: number, onDownloaded?: () => void) => {
  const [downloading, setDownloading] = useState<"orders" | "clients" | null>(null);
  const [downloadError, setDownloadError] = useState("");

  const download = async (kind: "orders" | "clients") => {
    setDownloading(kind);
    setDownloadError("");

    try {
      const blob =
        kind === "orders"
          ? await companyWithdrawalApiService.exportOrders(companyId)
          : await companyWithdrawalApiService.exportBusinessClients(companyId);

      saveBlob(blob, kind === "orders" ? "주문내역.xlsx" : "거래처기업정보.xlsx");
      onDownloaded?.();
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "다운로드에 실패했습니다.");
    } finally {
      setDownloading(null);
    }
  };

  return { downloading, download, downloadError };
};

/* ==================== COMPANY_MANAGER · USER : 회원 탈퇴 ==================== */

const WITHDRAW_REASONS = [
  "더 이상 이용하지 않아요.",
  "서비스가 불만족스러워요.",
  "사이트 이용이 어려워요.",
  "이용 요금이 부담돼요.",
  "다른 계정으로 중복가입되어 있어요.",
  "기타",
];

const MemberWithdrawal: React.FC = () => {
  const { user, logout } = useAuthStore();
  const isManager = user?.role === "COMPANY_MANAGER";

  const [selected, setSelected] = useState(WITHDRAW_REASONS[0]);
  const [etcReason, setEtcReason] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const isEtc = selected === "기타";

  const submit = async () => {
    setError("");

    try {
      await companyWithdrawalApiService.withdrawMember(isEtc ? etcReason : selected);
      setConfirming(false);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "탈퇴에 실패했습니다.");
      setConfirming(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10">
        <div className="w-[52px] h-[52px] rounded-full bg-[#E8EFFF] text-[#4285F4] text-[26px] flex items-center justify-center mx-auto mb-4">
          ✓
        </div>
        <h2 className="text-[20px] font-bold text-[#222] mb-2.5">
          탈퇴 신청이 접수되었습니다.
        </h2>
        <p className="text-[14px] text-[#555] mb-6">그동안 이용해 주셔서 감사합니다.</p>
        <Notice tone="info">
          지금부터 <b className="font-bold">로그인이 제한</b>됩니다. 7일이 지나면 계정이 최종
          삭제되며, 그 전에 철회를 원하시면 <b className="font-bold">admin@datoz.co.kr</b> 로 문의해
          주세요.
        </Notice>
        <button
          type="button"
          onClick={() => logout()}
          className="w-full py-3 rounded-[8px] text-[14px] font-medium bg-white text-[#222] border border-[#222] hover:bg-[#f5f5f5]"
        >
          확인 후 로그아웃
        </button>
      </div>
    );
  }

  return (
    <>
      <h3 className="text-[16px] font-bold text-[#222] mb-3">탈퇴 사유를 알려주세요.</h3>
      <div className="flex flex-wrap gap-2 mb-3">
        {WITHDRAW_REASONS.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setSelected(item)}
            className={`px-3.5 py-2 rounded-[8px] border text-[13px] ${
              selected === item
                ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4] font-medium"
                : "border-[#e0e0e0] bg-white text-[#555] hover:bg-[#fafafa]"
            }`}
          >
            {item}
          </button>
        ))}
      </div>
      <textarea
        rows={3}
        disabled={!isEtc}
        value={etcReason}
        onChange={(e) => setEtcReason(e.target.value)}
        placeholder="'기타'를 선택하면 사유를 입력할 수 있습니다."
        className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[14px] focus:border-[#4285F4] focus:outline-none disabled:bg-[#f5f5f5] disabled:text-[#bbb]"
      />

      <h3 className="text-[16px] font-bold text-[#222] mt-7 mb-3">탈퇴 전 확인해 주세요.</h3>
      <Notice tone="info">
        탈퇴 신청 후 <b className="font-bold">7일간의 유예 기간</b>이 지나면 계정이 최종 삭제됩니다.
        <ul className="list-disc pl-[18px] mt-2">
          <li>유예 기간 동안 로그인할 수 없습니다. (일반 · 소셜 로그인 모두)</li>
          <li>같은 이메일로 재가입할 수 없습니다.</li>
          <li>철회를 원하시면 유예 기간 안에 admin@datoz.co.kr 로 문의해 주세요.</li>
          <li>개인정보는 법정 보관을 위해 암호화 상태로 5년간 보관 후 자동 파기됩니다.</li>
        </ul>
      </Notice>

      {isManager && (
        <p className="text-[13px] text-[#888] -mt-1 mb-4">
          ※ 담당하시던 회사의 데이터는 삭제되지 않으며, 회사 계정은 그대로 유지됩니다.
        </p>
      )}

      <AgreeCheck
        checked={agreed}
        onChange={setAgreed}
        label="위 내용을 모두 확인했으며, 7일 후 계정이 삭제되는 것에 동의합니다. (필수)"
      />

      {error && <Notice tone="danger">{error}</Notice>}

      <button
        type="button"
        disabled={!agreed || (isEtc && !etcReason.trim())}
        onClick={() => setConfirming(true)}
        className={`w-full py-3 rounded-[8px] text-[14px] font-medium mt-6 ${
          agreed && !(isEtc && !etcReason.trim())
            ? "bg-[#222] text-white hover:bg-[#333]"
            : "bg-[#e0e0e0] text-[#bbb] cursor-not-allowed"
        }`}
      >
        회원 탈퇴하기
      </button>

      {confirming && (
        <ConfirmModal
          title="정말 탈퇴하시겠습니까?"
          message="탈퇴 후 7일간 로그인할 수 없으며, 7일이 지나면 계정이 최종 삭제됩니다. 같은 이메일로 재가입할 수 없습니다."
          confirmLabel="탈퇴하기"
          onClose={() => setConfirming(false)}
          onConfirm={submit}
        />
      )}
    </>
  );
};

/* ==================== 공용 조각 ==================== */

const ChecklistCard: React.FC<{
  step: number;
  title: string;
  satisfied: boolean;
  badge: { label: string; tone: "ok" | "gray" | "danger" };
  description: string;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
}> = ({ step, title, satisfied, badge, description, actionLabel, onAction, disabled }) => (
  <div
    className={`rounded-[12px] border p-4 mb-2.5 transition-colors ${
      satisfied ? "border-[#dbe6fb] bg-[#F7FAFF]" : "border-[#e0e0e0] bg-white"
    }`}
  >
    <div className="flex items-center gap-3.5">
      {/* 끝난 항목은 체크로, 남은 항목은 순번으로 보여 준다. */}
      <span
        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[14px] font-bold ${
          satisfied ? "bg-[#4285F4] text-white" : "bg-[#f0f0f0] text-[#888]"
        }`}
      >
        {satisfied ? "✓" : step}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className={`text-[15px] font-medium ${satisfied ? "text-[#555]" : "text-[#222]"}`}>
            {title}
          </h4>
          {badge.label && (
            <span
              className={`px-2 py-0.5 rounded-[4px] text-[11px] font-medium ${
                badge.tone === "ok"
                  ? "bg-[#E8EFFF] text-[#4285F4]"
                  : "bg-[#f0f0f0] text-[#888]"
              }`}
            >
              {badge.label}
            </span>
          )}
        </div>
        <p className="text-[12.5px] text-[#888] mt-1 leading-relaxed">{description}</p>
      </div>

      <button
        type="button"
        onClick={onAction}
        disabled={disabled}
        className={`shrink-0 px-3.5 py-2 rounded-[6px] text-[13px] font-medium transition-colors ${
          satisfied
            ? "border border-[#e0e0e0] text-[#888] hover:bg-[#f5f5f5]"
            : "bg-[#4285F4] text-white hover:bg-[#3367D6]"
        } disabled:bg-[#e8e8e8] disabled:text-[#bbb] disabled:border-[#e8e8e8]`}
      >
        {actionLabel}
      </button>
    </div>
  </div>
);

const AgreeCheck: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  label: string;
}> = ({ checked, disabled, onChange, label }) => (
  <label
    className={`flex items-start gap-2.5 text-[13px] mb-2.5 ${
      disabled ? "text-[#bbb] cursor-not-allowed" : "text-[#555] cursor-pointer"
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-[3px] w-[15px] h-[15px] accent-[#4285F4]"
    />
    {label}
  </label>
);

const Notice: React.FC<{ tone: "info" | "danger"; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <div
    className={`rounded-[8px] px-4 py-3.5 text-[13px] leading-[1.7] mb-4 border ${
      tone === "danger"
        ? "bg-[#fafafa] text-[#555] border-[#e0e0e0]"
        : "bg-[#EBF3FF] text-[#4285F4] border-[#4285F4]/20"
    }`}
  >
    {children}
  </div>
);

const ConfirmModal: React.FC<{
  title: string;
  message: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ title, message, confirmLabel, onClose, onConfirm }) => (
  <ModalFrame onClose={onClose}>
    <h3 className="text-[16px] font-bold text-[#222] mb-2.5">{title}</h3>
    <p className="text-[13px] text-[#555] mb-5">{message}</p>
    <div className="flex gap-2 justify-end">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 rounded-[6px] text-[13px] border border-[#222] text-[#222] hover:bg-[#f5f5f5]"
      >
        취소
      </button>
      <button
        type="button"
        onClick={onConfirm}
        className={`px-4 py-2 rounded-[6px] text-[13px] text-white ${
          "bg-[#222] hover:bg-[#333]"
        }`}
      >
        {confirmLabel}
      </button>
    </div>
  </ModalFrame>
);

/**
 * 되돌릴 수 없는 작업이라 사이트명을 직접 입력해야 진행된다.
 */
const ConfirmByNameModal: React.FC<{
  companyName: string;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ companyName, onClose, onConfirm }) => {
  const [typed, setTyped] = useState("");
  const matched = typed.trim() === companyName.trim() && companyName.trim() !== "";

  return (
    <ModalFrame onClose={onClose}>
      <h3 className="text-[16px] font-bold text-[#222] mb-2.5">
        정말 사이트 운영을 종료하시겠습니까?
      </h3>
      <p className="text-[13px] text-[#555] mb-4">
        신청하면 즉시 신규 주문이 차단되고, 7일이 지나면 소속 회원 계정과 회사 데이터가
        삭제됩니다. 계속하려면 사이트명을 정확히 입력해 주세요.
      </p>
      <span className="block text-[12px] font-medium text-[#555] mb-1.5">
        사이트명 : {companyName || "-"}
      </span>
      <input
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={companyName}
        className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[14px] focus:border-[#4285F4] focus:outline-none"
      />
      <div className="flex gap-2 justify-end mt-5">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-[6px] text-[13px] border border-[#222] text-[#222] hover:bg-[#f5f5f5]"
        >
          취소
        </button>
        <button
          type="button"
          disabled={!matched}
          onClick={onConfirm}
          className={`px-4 py-2 rounded-[6px] text-[13px] text-white ${
            matched ? "bg-[#222] hover:bg-[#333]" : "bg-[#e0e0e0] text-[#bbb] cursor-not-allowed"
          }`}
        >
          종료 신청
        </button>
      </div>
    </ModalFrame>
  );
};

const ModalFrame: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({
  onClose,
  children,
}) => (
  <div className="fixed inset-0 z-[6000] flex items-center justify-center p-5">
    <div className="absolute inset-0 bg-black opacity-50" onClick={onClose} />
    <div className="relative z-[6001] bg-white rounded-[12px] p-6 w-full max-w-[460px]">
      {children}
    </div>
  </div>
);

/* ==================== 표시용 헬퍼 ==================== */

const noticeBadge = (
  checklist: ClosingChecklist | null
): { label: string; tone: "ok" | "gray" | "danger" } => {
  if (!checklist?.noticePublishedAt) return { label: "공지 없음", tone: "gray" };
  // 체크 아이콘과 아래 설명이 이미 알려 주므로 뱃지를 따로 붙이지 않는다.
  if (checklist.noticeSatisfied) return { label: "", tone: "ok" };
  return { label: "1개월 미경과", tone: "danger" };
};

const noticeDescription = (checklist: ClosingChecklist | null): string => {
  if (!checklist?.noticePublishedAt) {
    return "운영 종료 공지를 게시하고 1개월이 지나야 신청할 수 있습니다.";
  }

  const published = `${formatDate(checklist.noticePublishedAt)} 게시`;
  if (checklist.noticeSatisfied) return `${published} · 1개월 경과`;

  const remaining = daysUntil(checklist.noticeSatisfiedAt);
  return remaining === null
    ? published
    : `${published} · 신청 가능까지 ${remaining}일 남았습니다.`;
};

const exportBadge = (
  exportedAt: string | null | undefined,
  satisfied: boolean | undefined
): { label: string; tone: "ok" | "gray" | "danger" } => {
  if (!exportedAt) return { label: "미다운로드", tone: "gray" };
  if (!satisfied) return { label: "유효 기간 만료", tone: "danger" };

  const days = daysSince(exportedAt);
  if (days === null) return { label: "완료", tone: "ok" };
  return { label: days === 0 ? "오늘 완료" : `${days}일 전 완료`, tone: "ok" };
};

const formatDate = (value: string | null): string => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("ko-KR");
};

const formatDateTime = (value: string | null): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.toLocaleDateString("ko-KR")} ${date.toLocaleTimeString("ko-KR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

/** 남은 일수. 이미 지났으면 0 */
const daysUntil = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.ceil((target - Date.now()) / (1000 * 60 * 60 * 24)));
};

/** 지난 일수 */
const daysSince = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const from = new Date(value).getTime();
  if (Number.isNaN(from)) return null;
  return Math.max(0, Math.floor((Date.now() - from) / (1000 * 60 * 60 * 24)));
};

const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export default Unregister;
